import './index.scss';
import React, {FunctionComponent, useCallback, useEffect, useState} from "react";
import {BizkeytechReduxActions, useBizkeytechDispatch, useBizkeytechSelector} from "../../redux";
import {Dialog, DialogTitle} from "@mui/material";
import classNames from "classnames";
import SubmitTicketForm from "./submit-ticket-form";
import SubmittedTicketSection from "./submitted-ticket-section";

export interface SubmitTicketDialogSubmissionResult {
    code: string,
    link: string,
}


const SubmitTicketDialog: FunctionComponent = () => {
    const dispatch = useBizkeytechDispatch();
    const state = useBizkeytechSelector(state => state.dialogs?.submitTicket);
    const [submissionResult, setSubmissionResult] = useState<SubmitTicketDialogSubmissionResult>();

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;

    /**
     * As soon as the dialog is closed:
     * - reset the submission result.
     */
    useEffect(() => {
        if (!state?.open) {
            setSubmissionResult(undefined)
        }
    }, [state?.open]);

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback(() => {
        if (state?.onClose) {
            state.onClose(!!submissionResult ? "successful" : "leave");
        }
        dispatch(BizkeytechReduxActions.dialogs.submitTicket({open: false}));
    }, [state, dispatch, submissionResult])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick)
                    closeDialog();
                break;
            case "closeClick":
                if (!state.hideCloseButton)
                    closeDialog();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown)
                    closeDialog();
                break;
            default:
                break;
        }
    }, [closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])


    return (
        <>
            <Dialog
                {...dialogProps}
                classes={{
                    ...(dialogProps.classes ?? {}),
                    root: classNames('submit-ticket-dialog-root', dialogProps.classes?.root),
                }}
                onClose={onDialogClosed}
            >
                <DialogTitle fontWeight={600} textAlign={'center'}>
                    {
                        submissionResult
                            ? "Ticket Submitted"
                            : "Submit a Ticket"
                    }
                </DialogTitle>
                {
                    !!submissionResult &&
                    <SubmittedTicketSection {...submissionResult}/>
                }
                {
                    !submissionResult &&
                    <SubmitTicketForm
                        data={state.data!}
                        setSubmissionResult={setSubmissionResult}
                    />
                }
            </Dialog>
        </>
    )
}

export default SubmitTicketDialog;
